import { HttpClient, HttpHeaders } from "@angular/common/http";
import { AngularFirestore } from "@angular/fire/firestore";
import { BehaviorSubject } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { User } from "../models";
import { AuthService } from "../services";
import { UserBuilder } from "./user.builder";

export class UserFactory {
  public users$: BehaviorSubject<User[]>;
  public builder: UserBuilder;

  constructor(
    public db: AngularFirestore,
    public httpClient: HttpClient,
    private authService: AuthService
  ) {
    this.users$ = new BehaviorSubject([]);
    this.getUsersOnInitialization();
  }

  private async getUsersOnInitialization(): Promise<void> {
    const userJWT = await this.authService.user.getIdToken();

    this.httpClient
    .get<User[]>(`${environment.apiBaseUrl}/api/v1/users/admins`, {
      headers: new HttpHeaders().set("Authorization", `${userJWT}`),
    })
    .pipe(
      catchError(err => {
        throw err;
      })
    )
    .subscribe({
      next: users =>
        this.users$.next(
          users
            .filter(user => user.deletedAt === null || !user.deletedAt)
            .map(user => new UserBuilder().load(user).build())
        ),
      error: err => console.error(err)
    });
  }

  public getAdmins() {
    return this.users$;
  }

  public async addUser(user: User) {
    const userJWT = await this.authService.user.getIdToken();

    this.httpClient
      .post<any>(
        `${environment.apiBaseUrl}/api/v1/users/admin`,
        { ...user, ...user.admin },
        {
          headers: new HttpHeaders().set("Authorization", `${userJWT}`)
        }
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      )
      .subscribe(user =>
        this.users$.next([
          ...this.users$.value,
          new UserBuilder().load(user).build()
        ])
      );
  }

  public async deleteUser(user: User) {
    const userJWT = await this.authService.user.getIdToken();

    this.httpClient
      .delete<{ id: string }>(
        `${environment.apiBaseUrl}/api/v1/users/${user.id}`,
        { headers: new HttpHeaders().set("Authorization", `${userJWT}`) }
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      )
      .subscribe(deletedUser => {
        let users = this.users$.value.filter(u => u.id !== user.id);
        this.users$.next(users);
      });
  }

  public async updateUser(user: User) {
    const userJWT = await this.authService.user.getIdToken();
    
    this.httpClient
      .put<User>(
        `${environment.apiBaseUrl}/api/v1/users`,
        { id: user.id, data: user },
        { headers: new HttpHeaders().set("Authorization", `${userJWT}`) }
      )
      .pipe(
        catchError(err => {
          throw err;
        })
      )
      .subscribe(updatedUser => {
        let users = this.users$.value;
        const user = new UserBuilder().load(updatedUser).build();
        const i = users.findIndex(u => u.id == user.id);
        users[i] = user;
        this.users$.next(users);
      });
  }
}
